// Retailer Deliveries — kitchens going OUT to customers.
//
// Spec: TKOS_MASTER_CONTEXT.md §Retailer Deliveries +
// inventory_sections/01_retailer.md §Retailer Deliveries.
// Source-of-truth render lives in portal-standalone.html lines:
//   2576 — page-delivery section (header + toolbar + info banner)
//   6525 — delivery state, render and modal helpers
//   6549 — rowHtml (the card)
//   6584 — "Going out to customers" group heading
//
// Backend
// -------
// kitchenos-api ships the dispatch addon at /var/www/kitchenos-api/addons/delivery,
// which mounts admin routes (/api/admin/deliveries — see addons/delivery/routes/
// admin-delivery.js:20) and a public courier-tracking surface (/api/track/:token
// — see addons/delivery/routes/track.js:102). There is NO retailer-scoped
// /api/portal/deliveries endpoint at the time of extraction; this page therefore
// calls the intended retailer endpoints (see TODO comments) and gracefully
// falls back to an in-memory empty list on 404 so the page still renders.
//
// Address rendering
// -----------------
// `delivery_address` is a jsonb column shaped like the customer address in CRM.
// We use the SAME `formatAddress` helper imported from the CRM page so we never
// produce `[object Object]`.
//
// KOS reference UX
// ----------------
// The reference (e.g. KOS-3E31FB71E2FA) is rendered in a monospace pill with
// a copy button — `navigator.clipboard.writeText(ref)` fires a success toast.
//
// No live-tracking websocket is wired up here (that's an addon for later).

import { ApiError } from "@tkos/api-client";
import { confirmDialog, iconMarkup } from "@tkos/shared-ui";
import type { DeliveryStatus, Carrier } from "@tkos/tkos-types";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

// Re-use the CRM page's defensive jsonb-or-string formatter so we never render
// `[object Object]` for an address field. Pulling it from the CRM module is
// the single-source-of-truth pattern the inventory mandates.
import { formatAddress } from "../crm/page";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

/** Server shape we expect from /api/portal/deliveries (or /api/admin/deliveries). */
interface ServerDelivery {
  id: string;
  /** KOS tracking reference, e.g. "KOS-3E31FB71E2FA". */
  ref?: string | null;
  customer?: string | { name?: string | null } | null;
  description?: string | null;
  /** jsonb { line1, line2, city, county, postcode } OR plain string. */
  address?: unknown;
  /** Unix ms or ISO. */
  eta?: number | string | null;
  status?: DeliveryStatus | string | null;
  value?: number | string | null;
  notes?: string | null;
  /** Optional courier metadata (we only display if present). */
  carrier?: Carrier | string | null;
  /** Public tracking token for /api/track/:token. */
  trackToken?: string | null;
}

/** Normalised in-memory shape used by this page. */
interface Delivery {
  id: string;
  ref: string;
  customer: string;
  description: string;
  /** Pre-formatted, single-line address (or "—" if absent/empty). */
  address: string;
  etaMs: number | null;
  status: DeliveryStatus;
  value: number;
  notes: string;
  carrier: Carrier | null;
  trackToken: string | null;
}

interface NewDeliveryForm {
  ref: string;
  customer: string;
  /** UUID of the picked CRM customer, when the typed name matches a known contact. */
  customerId: string | null;
  description: string;
  address: string;
  etaMs: number;
  value: number;
  notes: string;
}

type FilterValue = "all" | DeliveryStatus;

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<DeliveryStatus, string> = {
  scheduled: "Scheduled",
  in_transit: "In Transit",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  failed: "Failed",
};

const FILTER_OPTIONS: ReadonlyArray<{ value: FilterValue; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_transit", label: "In Transit" },
  { value: "out_for_delivery", label: "Out for Delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
];

const VALID_STATUSES = new Set<DeliveryStatus>([
  "scheduled",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "failed",
]);

const VALID_CARRIERS = new Set<Carrier>([
  "dpd",
  "apc",
  "dhl",
  "royal_mail",
  "own_vehicle",
]);

// TKOS icons reused in the address line + ref-copy button.
const ICON_PIN = iconMarkup("pin", { size: "sm" });
const ICON_COPY = iconMarkup("copy", { size: "sm" });

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const ETA_FMT = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

function formatEta(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  return ETA_FMT.format(new Date(ms));
}

function formatValue(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return "—";
  return GBP.format(v);
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Default the date/time picker to today + 5 days at 10:00 (matches source). */
function defaultDateTimeLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 5);
  d.setHours(10, 0, 0, 0);
  return (
    d.getFullYear() +
    "-" +
    pad(d.getMonth() + 1) +
    "-" +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    ":" +
    pad(d.getMinutes())
  );
}

/** Lightweight HTML escape for static-ish list-card markup. */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Coercion / normalisation ─────────────────────────────────────────────────

function coerceStatus(raw: unknown): DeliveryStatus {
  if (typeof raw === "string") {
    if (VALID_STATUSES.has(raw as DeliveryStatus)) return raw as DeliveryStatus;
    // Tolerate the legacy "delayed" label as a generic "failed".
    const lc = raw.trim().toLowerCase();
    if (lc === "delayed") return "failed";
    if (lc === "en_route") return "in_transit";
  }
  return "scheduled";
}

function coerceCarrier(raw: unknown): Carrier | null {
  if (typeof raw !== "string") return null;
  return VALID_CARRIERS.has(raw as Carrier) ? (raw as Carrier) : null;
}

function coerceMs(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return null;
}

function coerceValue(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const cleaned = raw.replace(/[^0-9.]/g, "");
    const n = Number.parseFloat(cleaned);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function coerceCustomer(raw: unknown): string {
  if (typeof raw === "string") return raw.trim();
  if (raw && typeof raw === "object") {
    const r = raw as { name?: unknown };
    if (typeof r.name === "string") return r.name.trim();
  }
  return "";
}

function normaliseDelivery(d: ServerDelivery): Delivery {
  return {
    id: String(d.id ?? ""),
    ref: String(d.ref ?? "").trim(),
    customer: coerceCustomer(d.customer),
    description: String(d.description ?? "").trim(),
    address: formatAddress(d.address),
    etaMs: coerceMs(d.eta),
    status: coerceStatus(d.status),
    value: coerceValue(d.value),
    notes: String(d.notes ?? "").trim(),
    carrier: coerceCarrier(d.carrier),
    trackToken: typeof d.trackToken === "string" && d.trackToken.trim()
      ? d.trackToken.trim()
      : null,
  };
}

// ── Mount ────────────────────────────────────────────────────────────────────

const deliveriesPage: Page = {
  name: "deliveries",
  label: "Deliveries",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let deliveries: Delivery[] = [];
    let filter: FilterValue = "all";
    let loadingError: string | null = null;
    let isLoading = true;

    // ── DOM scaffold ────────────────────────────────────────────────────────

    const root = document.createElement("section");
    root.className = "del-page";
    root.innerHTML = `
      <header class="del-header">
        <h1>Retailer Deliveries</h1>
        <p>Kitchens going out to your customers &mdash; schedule, dispatch, and confirm.</p>
      </header>

      <div class="del-toolbar">
        <select class="del-filter" data-role="filter" aria-label="Filter deliveries by status">
          ${FILTER_OPTIONS.map(
            (o) => `<option value="${o.value}">${esc(o.label)}</option>`,
          ).join("")}
        </select>
        <span class="del-toolbar-spacer"></span>
        <button class="del-btn del-btn--ghost" type="button" data-role="checklist">Goods-out checklist</button>
        <button class="del-btn del-btn--ghost" type="button" data-role="log-supplier">Log supplier delivery</button>
        <button class="del-btn del-btn--primary" type="button" data-role="new">+ New customer delivery</button>
      </div>

      <div class="del-banner" role="note">
        ${iconMarkup("info", { size: "sm" })}
        <span>Most suppliers send delivery updates by email or text. <strong>Coming soon</strong>: those updates will land in your portal Messages inbox automatically. For now, log them manually with <em>Log supplier delivery</em>.</span>
      </div>

      <h2 class="del-section-heading" data-role="section-heading">Going out to customers</h2>
      <div class="del-list" data-role="list" aria-live="polite"></div>
    `;
    container.appendChild(root);

    const filterEl = root.querySelector<HTMLSelectElement>("[data-role='filter']")!;
    const listEl = root.querySelector<HTMLDivElement>("[data-role='list']")!;
    const headingEl = root.querySelector<HTMLHeadingElement>("[data-role='section-heading']")!;
    const newBtn = root.querySelector<HTMLButtonElement>("[data-role='new']")!;
    const checklistBtn = root.querySelector<HTMLButtonElement>("[data-role='checklist']")!;
    const logSupplierBtn = root.querySelector<HTMLButtonElement>("[data-role='log-supplier']")!;

    // ── Modal: New customer delivery ────────────────────────────────────────

    const dialog = document.createElement("dialog");
    dialog.className = "del-modal";
    dialog.innerHTML = `
      <form method="dialog" class="del-modal__form" data-role="form" novalidate>
        <header class="del-modal__head">
          <h2>New customer delivery</h2>
          <button class="del-modal__close" type="button" data-role="close" aria-label="Close">&times;</button>
        </header>
        <div class="del-modal__body">
          <div class="del-field" data-field="customer">
            <label for="del-customer">Customer name</label>
            <input id="del-customer" name="customer" type="text" autocomplete="off" list="del-customer-list" placeholder="Start typing or pick a customer…" required />
            <datalist id="del-customer-list"></datalist>
            <span class="del-field__error">Customer name is required.</span>
          </div>
          <div class="del-field">
            <label for="del-ref">KOS tracking reference</label>
            <input id="del-ref" name="ref" type="text" placeholder="KOS-…" />
          </div>
          <div class="del-field">
            <label for="del-desc">Kitchen description <span class="del-field__hint">(optional)</span></label>
            <input id="del-desc" name="description" type="text" placeholder="e.g. Handleless Matt Graphite" />
          </div>
          <div class="del-field">
            <label for="del-address">Delivery address <span class="del-field__hint">(optional)</span></label>
            <input id="del-address" name="address" type="text" autocomplete="street-address" placeholder="e.g. 42 Elm Road, Harrogate, HG1 1AA" />
          </div>
          <div class="del-field-row">
            <div class="del-field">
              <label for="del-eta">ETA</label>
              <input id="del-eta" name="eta" type="datetime-local" required />
            </div>
            <div class="del-field">
              <label for="del-value">Job value (£)</label>
              <input id="del-value" name="value" type="number" min="0" step="100" placeholder="0" />
            </div>
          </div>
          <div class="del-field">
            <label for="del-notes">Notes <span class="del-field__hint">(optional)</span></label>
            <input id="del-notes" name="notes" type="text" placeholder="Any additional details…" />
          </div>
        </div>
        <footer class="del-modal__footer">
          <button type="button" class="del-btn" data-role="cancel">Cancel</button>
          <button type="submit" class="del-btn del-btn--primary" data-role="save">Schedule delivery</button>
        </footer>
      </form>
    `;
    root.appendChild(dialog);

    const formEl = dialog.querySelector<HTMLFormElement>("[data-role='form']")!;
    const customerField = dialog.querySelector<HTMLDivElement>("[data-field='customer']")!;
    const customerInput = dialog.querySelector<HTMLInputElement>("#del-customer")!;
    const customerList = dialog.querySelector<HTMLDataListElement>("#del-customer-list")!;
    const refInput = dialog.querySelector<HTMLInputElement>("#del-ref")!;
    const descInput = dialog.querySelector<HTMLInputElement>("#del-desc")!;
    const addressInput = dialog.querySelector<HTMLInputElement>("#del-address")!;
    const etaInput = dialog.querySelector<HTMLInputElement>("#del-eta")!;
    const valueInput = dialog.querySelector<HTMLInputElement>("#del-value")!;
    const notesInput = dialog.querySelector<HTMLInputElement>("#del-notes")!;
    const saveBtn = dialog.querySelector<HTMLButtonElement>("[data-role='save']")!;
    const cancelBtn = dialog.querySelector<HTMLButtonElement>("[data-role='cancel']")!;
    const closeBtn = dialog.querySelector<HTMLButtonElement>("[data-role='close']")!;

    function generateRef(): string {
      // Crypto-grade-ish 12-char hex suffix, mirrors source-of-truth pattern.
      const buf = new Uint8Array(6);
      (globalThis.crypto ?? window.crypto).getRandomValues(buf);
      return (
        "KOS-" +
        Array.from(buf, (b) => b.toString(16).padStart(2, "0").toUpperCase()).join("")
      );
    }

    function resetForm(): void {
      customerInput.value = "";
      refInput.value = generateRef();
      descInput.value = "";
      addressInput.value = "";
      etaInput.value = defaultDateTimeLocal();
      valueInput.value = "";
      notesInput.value = "";
      customerField.classList.remove("del-field--invalid");
    }

    function openModal(): void {
      resetForm();
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
      window.setTimeout(() => customerInput.focus(), 60);
    }

    function closeModal(): void {
      if (dialog.open) dialog.close();
    }

    // ── Render ──────────────────────────────────────────────────────────────

    function renderList(): void {
      if (isLoading) {
        listEl.innerHTML = `<div class="del-state">Loading deliveries…</div>`;
        headingEl.dataset.count = "";
        return;
      }
      if (loadingError) {
        listEl.innerHTML = `<div class="del-state del-state--error">${esc(loadingError)}</div>`;
        return;
      }

      const filtered =
        filter === "all" ? deliveries : deliveries.filter((d) => d.status === filter);

      headingEl.dataset.count = String(filtered.length);
      headingEl.textContent = `Going out to customers (${filtered.length})`;

      if (filtered.length === 0) {
        listEl.innerHTML = `<div class="del-state">No deliveries yet — click <strong>+ New customer delivery</strong> to add one.</div>`;
        return;
      }

      // Sort soonest-first (matches source-of-truth).
      const sorted = filtered.slice().sort((a, b) => {
        const aMs = a.etaMs ?? Number.POSITIVE_INFINITY;
        const bMs = b.etaMs ?? Number.POSITIVE_INFINITY;
        return aMs - bMs;
      });

      listEl.innerHTML = sorted.map(renderCard).join("");
    }

    function renderCard(d: Delivery): string {
      const statusLabel = STATUS_LABELS[d.status];
      const eta = formatEta(d.etaMs);
      const value = formatValue(d.value);

      const refHtml = d.ref
        ? `<button type="button" class="del-card__ref" data-action="copy-ref" data-ref="${esc(d.ref)}" title="Copy KOS reference">
             <span class="del-card__ref-text">${esc(d.ref)}</span>
             <span class="del-card__ref-copy" aria-label="Copy">${ICON_COPY}</span>
           </button>`
        : `<span class="del-card__ref del-card__ref--missing">No reference</span>`;

      const addressHtml = d.address && d.address !== "—"
        ? `<div class="del-card__address">
             ${ICON_PIN}
             <span>${esc(d.address)}</span>
           </div>`
        : "";

      const descHtml = d.description
        ? `<div class="del-card__desc">${esc(d.description)}</div>`
        : "";

      const notesHtml = d.notes
        ? `<div class="del-card__notes">&ldquo;${esc(d.notes)}&rdquo;</div>`
        : "";

      const customerHtml = d.customer
        ? `<span class="del-card__customer" data-customer-name="${esc(d.customer)}" style="cursor:pointer">${esc(d.customer)}</span>`
        : `<span class="del-card__customer del-card__customer--missing">Unnamed customer</span>`;

      const valueHtml = value !== "—"
        ? `<div class="del-card__value">${esc(value)}</div>`
        : "";

      return `
        <article class="del-card" data-id="${esc(d.id)}" data-status="${d.status}">
          <span class="del-card__bar" aria-hidden="true"></span>
          <div class="del-card__body">
            <div class="del-card__top">
              <div class="del-card__top-left">
                ${refHtml}
                <span class="del-badge del-badge--direction">To customer</span>
                ${customerHtml}
              </div>
              ${valueHtml}
            </div>
            ${descHtml}
            ${addressHtml}
            <div class="del-card__meta">
              <span class="del-card__eta">ETA ${esc(eta)}</span>
              <span class="del-badge" data-status="${d.status}">${esc(statusLabel)}</span>
            </div>
            ${notesHtml}
          </div>
          <div class="del-card__actions">
            <button type="button" class="del-action" data-action="update" data-id="${esc(d.id)}">Update</button>
            <button type="button" class="del-action" data-action="checklist" data-id="${esc(d.id)}">Checklist</button>
            <button type="button" class="del-action del-action--danger" data-action="delete" data-id="${esc(d.id)}">Delete</button>
          </div>
        </article>
      `;
    }

    // ── Backend ─────────────────────────────────────────────────────────────

    // Lookup of picked customer name -> CRM UUID, populated by
    // loadCustomerOptions(). Lets handleSubmit resolve the typed/selected name
    // back to a customer id so the delivery links to the record + customer
    // portal (mirrors the customer-record page, which passes the id directly).
    const customerIdByName = new Map<string, string>();

    // Populate the Add-Delivery customer picker from the live CRM contacts
    // (same source the Bookings/CRM pages use). The field stays a free-text
    // input so a not-yet-in-CRM customer can still be typed — the datalist
    // just gives a dropdown of known customers to pick from. Best-effort: on
    // failure the field silently falls back to plain free-text.
    async function loadCustomerOptions(): Promise<void> {
      try {
        const resp = await ctx.api<{ contacts?: Array<{ id?: string; name?: string }> }>(
          "/crm/contacts",
          { method: "GET", signal: ac.signal },
        );
        const contacts = (Array.isArray(resp?.contacts) ? resp.contacts : [])
          .map((c) => ({ id: String(c?.id ?? "").trim(), name: String(c?.name ?? "").trim() }))
          .filter((c) => c.name.length > 0)
          .sort((a, b) => a.name.localeCompare(b.name));
        // De-dupe by name (case-insensitive) while preserving sort order, and
        // record the name -> id mapping so the picked customer can be linked.
        customerIdByName.clear();
        const seen = new Set<string>();
        customerList.innerHTML = contacts
          .filter((c) => {
            const key = c.name.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            if (c.id) customerIdByName.set(key, c.id);
            return true;
          })
          .map((c) => `<option value="${esc(c.name)}"></option>`)
          .join("");
      } catch (err) {
        if (isAbortError(err)) return;
        console.info("[deliveries] could not load customer list for picker", err);
      }
    }

    async function loadDeliveries(): Promise<void> {
      isLoading = true;
      loadingError = null;
      renderList();

      // TODO: real endpoint — once retailer scoping lands:
      //   GET /api/portal/deliveries  ->  { deliveries: ServerDelivery[] }
      // The dispatch addon currently exposes /api/admin/deliveries (admin-only,
      // see kitchenos-api/addons/delivery/routes/admin-delivery.js:20).
      try {
        const resp = await ctx.api<{ deliveries?: ServerDelivery[] }>(
          "/portal/deliveries",
          { method: "GET", signal: ac.signal },
        );
        const list = Array.isArray(resp?.deliveries) ? resp.deliveries : [];
        deliveries = list.map(normaliseDelivery);
      } catch (err) {
        if (isAbortError(err)) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 401)) {
          // Endpoint not implemented for retailers yet — render an empty state.
          console.info(
            "[deliveries] /api/portal/deliveries not available; rendering empty state.",
          );
          deliveries = [];
        } else {
          console.error("[deliveries] load failed", err);
          loadingError = "Could not load deliveries. Please try again.";
          ctx.showToast("Could not load deliveries", "error");
        }
      } finally {
        isLoading = false;
        renderList();
      }
    }

    async function createDeliveryApi(form: NewDeliveryForm): Promise<void> {
      // TODO: real endpoint:
      //   POST /api/portal/deliveries
      //   body: { ref, customer, description, address, eta, value, notes }
      const optimistic: Delivery = {
        id: `tmp-${Date.now()}`,
        ref: form.ref,
        customer: form.customer,
        description: form.description,
        address: form.address.trim() ? form.address.trim() : "—",
        etaMs: form.etaMs,
        status: "scheduled",
        value: form.value,
        notes: form.notes,
        carrier: null,
        trackToken: null,
      };

      try {
        const resp = await ctx.api<{ delivery: ServerDelivery }>(
          "/portal/deliveries",
          {
            method: "POST",
            signal: ac.signal,
            body: JSON.stringify({
              ref: form.ref,
              customer: form.customer,
              // Forward the resolved CRM id (when the picked name matches a
              // known contact) so the backend links the delivery to the
              // customer record + surfaces it in the customer portal. Without
              // this the delivery was stored with a NULL customer_id and became
              // invisible to both. Mirrors the customer-record page path.
              ...(form.customerId
                ? { customerId: form.customerId, direction: "to_customer" }
                : {}),
              description: form.description,
              address: form.address,
              eta: form.etaMs,
              value: form.value,
              notes: form.notes,
              status: "scheduled",
            }),
          },
        );
        if (resp?.delivery) {
          deliveries.push(normaliseDelivery(resp.delivery));
        } else {
          deliveries.push(optimistic);
        }
        ctx.showToast("Customer delivery scheduled", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 401)) {
          deliveries.push(optimistic);
          console.info(
            "[deliveries] POST /api/portal/deliveries not available; storing optimistically.",
          );
          ctx.showToast(
            "Delivery scheduled (local only — backend pending)",
            "info",
          );
        } else {
          console.error("[deliveries] create failed", err);
          ctx.showToast("Could not schedule delivery", "error");
          return;
        }
      }
      renderList();
    }

    async function updateStatusApi(id: string, next: DeliveryStatus): Promise<void> {
      // TODO: real endpoint:
      //   PATCH /api/portal/deliveries/:id  body: { status }
      try {
        await ctx.api<unknown>(`/portal/deliveries/${encodeURIComponent(id)}`, {
          method: "PATCH",
          signal: ac.signal,
          body: JSON.stringify({ status: next }),
        });
      } catch (err) {
        if (isAbortError(err)) return;
        if (!(err instanceof ApiError) || (err.status !== 404 && err.status !== 401)) {
          console.error("[deliveries] update failed", err);
          ctx.showToast("Could not update delivery", "error");
          return;
        }
        // 404/401 — backend not ready, fall through to local update.
        console.info("[deliveries] PATCH not available; updating locally only.");
      }
      const idx = deliveries.findIndex((d) => d.id === id);
      if (idx >= 0) {
        const cur = deliveries[idx];
        if (cur) {
          deliveries[idx] = { ...cur, status: next };
        }
      }
      ctx.showToast(`Status updated → ${STATUS_LABELS[next]}`, "info");
      renderList();
    }

    async function deleteDeliveryApi(id: string): Promise<void> {
      // TODO: real endpoint:
      //   DELETE /api/portal/deliveries/:id
      try {
        await ctx.api<unknown>(`/portal/deliveries/${encodeURIComponent(id)}`, {
          method: "DELETE",
          signal: ac.signal,
        });
      } catch (err) {
        if (isAbortError(err)) return;
        if (!(err instanceof ApiError) || (err.status !== 404 && err.status !== 401)) {
          console.error("[deliveries] delete failed", err);
          ctx.showToast("Could not delete delivery", "error");
          return;
        }
        console.info("[deliveries] DELETE not available; removing locally only.");
      }
      deliveries = deliveries.filter((d) => d.id !== id);
      ctx.showToast("Delivery deleted", "info");
      renderList();
    }

    // ── KOS reference: copy to clipboard ───────────────────────────────────

    async function copyRefToClipboard(ref: string): Promise<void> {
      const trimmed = ref.trim();
      if (!trimmed) return;
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          await navigator.clipboard.writeText(trimmed);
        } else {
          // Fallback for older browsers / non-secure contexts.
          const ta = document.createElement("textarea");
          ta.value = trimmed;
          ta.setAttribute("readonly", "");
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        ctx.showToast(`Copied ${trimmed}`, "success");
      } catch (err) {
        console.warn("[deliveries] copy failed", err);
        ctx.showToast("Could not copy reference", "error");
      }
    }

    // ── Status update prompt ───────────────────────────────────────────────

    function nextStatusFor(current: DeliveryStatus): DeliveryStatus {
      // Sensible "advance to next stage" mapping. Users can re-click to step
      // through the lifecycle without needing a dedicated edit modal.
      switch (current) {
        case "scheduled":
          return "in_transit";
        case "in_transit":
          return "out_for_delivery";
        case "out_for_delivery":
          return "delivered";
        case "delivered":
          return "scheduled";
        case "failed":
          return "scheduled";
        default:
          return "scheduled";
      }
    }

    function promptStatusUpdate(id: string): void {
      const cur = deliveries.find((d) => d.id === id);
      if (!cur) return;
      const next = nextStatusFor(cur.status);
      void (async () => {
        if (!(await confirmDialog(
          `Update delivery ${cur.ref || id} from "${STATUS_LABELS[cur.status]}" to "${STATUS_LABELS[next]}"?`,
          { title: "Update delivery", confirmLabel: "Update" },
        ))) return;
        void updateStatusApi(id, next);
      })();
    }

    // ── Form submit ─────────────────────────────────────────────────────────

    function handleSubmit(e: Event): void {
      e.preventDefault();
      const customer = customerInput.value.trim();
      if (!customer) {
        customerField.classList.add("del-field--invalid");
        customerInput.focus();
        return;
      }

      const etaRaw = etaInput.value;
      const etaMs = etaRaw ? new Date(etaRaw).getTime() : Date.now() + 86_400_000 * 5;
      const valueNum = Number.parseInt((valueInput.value || "0").replace(/[^0-9]/g, ""), 10);

      const form: NewDeliveryForm = {
        ref: refInput.value.trim() || generateRef(),
        customer,
        customerId: customerIdByName.get(customer.toLowerCase()) ?? null,
        description: descInput.value.trim(),
        address: addressInput.value.trim(),
        etaMs: Number.isFinite(etaMs) ? etaMs : Date.now() + 86_400_000 * 5,
        value: Number.isFinite(valueNum) ? valueNum : 0,
        notes: notesInput.value.trim(),
      };

      saveBtn.disabled = true;
      saveBtn.textContent = "Saving…";
      void createDeliveryApi(form).finally(() => {
        saveBtn.disabled = false;
        saveBtn.textContent = "Schedule delivery";
        closeModal();
      });
    }

    // ── Wiring ──────────────────────────────────────────────────────────────

    filterEl.value = filter;
    const onFilterChange = (): void => {
      filter = (filterEl.value as FilterValue) ?? "all";
      renderList();
    };
    filterEl.addEventListener("change", onFilterChange);

    const onNewClick = (): void => openModal();
    newBtn.addEventListener("click", onNewClick);

    const onChecklistClick = (): void => {
      // Source-of-truth opens a "Goods-out checklist" modal (portal-standalone.html
      // ~line 6557). The modular checklist component is a follow-up — for now
      // surface a friendly toast.
      ctx.showToast("Goods-out checklist — coming soon", "info");
    };
    checklistBtn.addEventListener("click", onChecklistClick);

    const onLogSupplierClick = (): void => {
      // Inbound supplier deliveries are explicitly out of scope per the spec
      // ("Supplier delivery updates coming soon"). Show the same notice.
      ctx.showToast("Supplier delivery logging — coming soon", "info");
    };
    logSupplierBtn.addEventListener("click", onLogSupplierClick);

    formEl.addEventListener("submit", handleSubmit);
    cancelBtn.addEventListener("click", () => closeModal());
    closeBtn.addEventListener("click", () => closeModal());

    // Click-outside-to-close.
    const onDialogClick = (e: MouseEvent): void => {
      if (e.target === dialog) closeModal();
    };
    dialog.addEventListener("click", onDialogClick);

    // List event delegation — Update / Checklist / Delete / Copy-ref.
    const onListClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const copyBtn = target.closest<HTMLButtonElement>("[data-action='copy-ref']");
      if (copyBtn) {
        e.preventDefault();
        const ref = copyBtn.dataset.ref ?? "";
        void copyRefToClipboard(ref);
        return;
      }

      const actionBtn = target.closest<HTMLButtonElement>("button.del-action");
      if (!actionBtn || actionBtn.disabled) return;
      const action = actionBtn.dataset.action;
      const id = actionBtn.dataset.id;
      if (!id) return;

      if (action === "update") {
        promptStatusUpdate(id);
      } else if (action === "checklist") {
        ctx.showToast("Per-delivery checklist — coming soon", "info");
      } else if (action === "delete") {
        void (async () => {
          if (!(await confirmDialog("Delete this delivery? This cannot be undone.", {
            title: "Delete delivery",
            confirmLabel: "Delete",
            destructive: true,
          }))) return;
          void deleteDeliveryApi(id);
        })();
      }
    };
    listEl.addEventListener("click", onListClick);

    // ── Initial load ────────────────────────────────────────────────────────

    void loadDeliveries();
    void loadCustomerOptions();

    // ── Cleanup ─────────────────────────────────────────────────────────────

    return () => {
      ac.abort();
      filterEl.removeEventListener("change", onFilterChange);
      newBtn.removeEventListener("click", onNewClick);
      checklistBtn.removeEventListener("click", onChecklistClick);
      logSupplierBtn.removeEventListener("click", onLogSupplierClick);
      formEl.removeEventListener("submit", handleSubmit);
      dialog.removeEventListener("click", onDialogClick);
      listEl.removeEventListener("click", onListClick);
      if (dialog.open) dialog.close();
    };
  },
};

registerPage(deliveriesPage);

export default deliveriesPage;
